import React, { useMemo, useRef, useState } from 'react';
import { DealerLocatorLandingModel } from '../../../../models/dealerLocatorLanding.model';
import {
  TAreaPolygonData,
  TCountry,
  TGeometry,
  TState,
} from '../../../../types/dealerLocatorLanding.types';
import { isGeometryDocType } from '../../../../dealerLocator.utils';
import { Polygon } from '../../../@reactGoogleMap/components/polygon';
import { debounceHandler } from '../../../../../../utils/debounceHandler.utils';
import {
  MAP_POLYGON_COLOR_ACTIVE,
  MAP_POLYGON_COLOR_DEFAULT,
  MAP_POLYGON_OPACITY_ACTIVE,
  MAP_POLYGON_OPACITY_DEFAULT,
} from '../../../../../../constants/dealerLocator.constants';

type TAreaPolygonProps = {
  data: TAreaPolygonData;
  country?: TCountry | null;
  setLabel: React.Dispatch<React.SetStateAction<string | null>>;
  setCountry?: (country: TCountry | null) => void;
  setRegion?: (region: TState | null) => void;
  model: DealerLocatorLandingModel;
};

const AreaPolygon = ({
  data,
  country,
  setLabel,
  setCountry,
  setRegion,
  model,
}: TAreaPolygonProps) => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const getGeometryWithLatLng = (geometry: TGeometry) => {
    return geometry?.coordinates?.[0]?.map((point) => new google.maps.LatLng(point[1], point[0]));
  };

  const polygonsWithLatLng = useMemo(() => {
    if (isGeometryDocType(data?.kml)) {
      return [
        {
          code: data.code,
          name: data.name,
          coordinates: getGeometryWithLatLng(data?.kml.geometry),
          key: `${data.code}-0`,
        },
      ];
    } else {
      return data?.kml.geometries.map((geometry, i) => ({
        code: data.code,
        name: data.name,
        coordinates: getGeometryWithLatLng(geometry),
        key: `${data.code}-${i}`,
      }));
    }
  }, [data]);

  const hasPolygons = polygonsWithLatLng.length > 0;
  const [active, setActive] = useState<boolean>(false);

  const regionPolygonOptions = {
    fillColor: MAP_POLYGON_COLOR_DEFAULT,
    fillOpacity: MAP_POLYGON_OPACITY_DEFAULT,
    strokeColor: 'transparent',
  };

  const activeRegionPolygonOptions = {
    fillColor: MAP_POLYGON_COLOR_ACTIVE,
    fillOpacity: MAP_POLYGON_OPACITY_ACTIVE,
    strokeColor: 'transparent',
  };

  const polygonOptions: google.maps.PolygonOptions = useMemo(() => {
    return active ? activeRegionPolygonOptions : regionPolygonOptions;
  }, [active]);

  return (
    <>
      {hasPolygons &&
        polygonsWithLatLng?.map((polygon) => (
          <Polygon
            polygonPaths={polygon.coordinates}
            onMouseOver={() => {
              debounceHandler(
                () => {
                  setLabel(polygon.name);
                  setActive(true);
                },
                timerRef,
                50
              );
            }}
            onMouseOut={() => {
              debounceHandler(
                () => {
                  setLabel(null);
                  setActive(false);
                },
                timerRef,
                50
              );
            }}
            onClick={() => {
              setLabel(null);
              if (setCountry) {
                setCountry(model.countries.find((c) => c.IsoCode === polygon.code) ?? null);
              } else if (setRegion) {
                const state =
                  country?.states.find((state) => state.gmiCode === polygon.code) ?? null;
                setRegion(state);
              }
            }}
            polygonOptions={polygonOptions}
            key={polygon.key}
          />
        ))}
    </>
  );
};

export default AreaPolygon;
